/**
 * SidebarGroup — aufklappbare Arbeitsbereich-Gruppe der Desktop-Sidebar
 * (Joerg 2026-09-17, Plan „Sidebar-Gruppen").
 *
 * Zwei Darstellungen, gesteuert von PortalSidebar:
 *  - breit (Akkordeon): Kopfzeile mit Icon/Label/Badge-Summe/Pin/Chevron, darunter
 *    die Eintraege, wenn `open`. Alle Gruppen sind standardmaessig ZU — auch die
 *    mit der aktiven Seite (die wird nur am Kopf hervorgehoben). Offen nur per
 *    Hover (Zeiger irgendwo in der Sidebar, siehe PortalSidebar) oder Pin.
 *  - schmal (Flyout): ein Icon-Button je Gruppe, Hover/Tipp oeffnet ein Panel
 *    rechts neben der Sidebar (createPortal + position:fixed, weil <aside>
 *    overflow-y:auto hat — feedback_overflow_hidden_portal_dropdown).
 *
 * Hover-/Click-Trennung (feedback_hover_click_conflict): Hover nur bei
 * `pointer: fine`, setzt einen ANDEREN Zustand (hover) als der Klick (pin) —
 * `open` ist das ODER beider, ein Klick kann also nie „unter dem Zeiger
 * zuklappen". Auf Touch gibt es keinen Hover, der Klick toggelt.
 * z-Index 40: unter Modals (z-50), ueber Sticky-Headern (z-30).
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
// Joerg 2026-09-17 (Nachtrag): Gruppenkoepfe muessen sich von den Eintraegen
// darunter abheben (Versalien, Trennlinie, kleineres Icon, Eintraege eingerueckt)
// und „leicht verzoegert aufswipen": PortalSidebar verzoegert das Hover-Oeffnen
// (HOVER_OPEN_MS), hier laeuft die weiche Ausklapp-Animation (grid-rows 0fr→1fr)
// bzw. das Einblenden des Flyouts. prefers-reduced-motion: keine Animation.
import { createPortal } from 'react-dom';
import { ChevronRight, Pin } from 'lucide-react';
import { getModalPortalTarget } from '@onvice/portal-ui';
import type { SidebarGroupIcon, SidebarGroupKey } from '../../lib/sidebar-groups';

export function isFinePointer(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches;
}

export interface SidebarGroupProps {
  slug: SidebarGroupKey;
  label: string;
  icon: SidebarGroupIcon;
  collapsed: boolean;
  /** breit: Eintraege sichtbar (editMode || pinned || hover) */
  open: boolean;
  pinned: boolean;
  /** Gruppe enthaelt die aktive Route — nur Hervorhebung, kein Auto-Open */
  active: boolean;
  editMode: boolean;
  /** Summe der Zahlen-Badges der Eintraege (breit nur zu, schmal immer) */
  badgeSum: number;
  badgeTitle?: string;
  /** Zusatz hinter dem Label, z. B. Anzahl im Mehr-Menue */
  countHint?: number;
  /** Hover auf Kopf (breit) bzw. Icon (schmal) — nur bei pointer: fine gefeuert */
  onHoverStart: (slug: SidebarGroupKey) => void;
  onHeadClick: (slug: SidebarGroupKey) => void;
  /** schmal: Flyout-Panel sichtbar */
  flyoutOpen: boolean;
  onFlyoutClose: () => void;
  onFlyoutEnter: () => void;
  onFlyoutLeave: () => void;
  children: ReactNode;
}

const RAIL_GAP = 4;

export default function SidebarGroup(props: SidebarGroupProps) {
  const {
    slug, label, icon: Icon, collapsed, open, pinned, active, editMode, badgeSum, badgeTitle, countHint,
    onHoverStart, onHeadClick, flyoutOpen, onFlyoutClose, onFlyoutEnter, onFlyoutLeave, children,
  } = props;
  const popKey = `sidebar-group-${slug}`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  // Flyout-Einblendung: erst positionieren, dann im naechsten Frame einblenden.
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!(collapsed && flyoutOpen)) { setShown(false); return; }
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, [collapsed, flyoutOpen]);

  // Flyout positionieren: rechts neben dem Icon, unten an den Viewport geklemmt.
  // Nachfuehren bei Scroll (capture, wegen overflow-y:auto am <aside>) + Resize —
  // Scrollen schliesst NICHT (Joerg 2026-09-17).
  useLayoutEffect(() => {
    if (!collapsed || !flyoutOpen) { setPos(null); return; }
    const compute = () => {
      const t = triggerRef.current; if (!t) return;
      const r = t.getBoundingClientRect();
      const h = panelRef.current?.offsetHeight ?? 0;
      const top = Math.max(64, Math.min(r.top, window.innerHeight - h - 8));
      setPos({ top, left: r.right + RAIL_GAP });
    };
    compute();
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [collapsed, flyoutOpen]);

  // Click-Outside + ESC fuer das Flyout (data-popover auf Trigger UND Panel,
  // weil das Portal kein DOM-Kind des Triggers ist).
  useEffect(() => {
    if (!collapsed || !flyoutOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target;
      if (!(t instanceof Element) || !t.closest(`[data-popover="${popKey}"]`)) onFlyoutClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onFlyoutClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [collapsed, flyoutOpen, popKey, onFlyoutClose]);

  const badge = badgeSum > 0 ? (badgeSum > 99 ? '99+' : String(badgeSum)) : null;
  const title = badgeTitle ? `${label} · ${badgeTitle}` : label;

  // ---------------------------------------------------------------- schmal
  if (collapsed) {
    return (
      <li data-sidebar-group={slug} data-popover={popKey} className="relative">
        <button
          ref={triggerRef}
          type="button"
          data-popover={popKey}
          aria-expanded={flyoutOpen}
          aria-label={label}
          title={title}
          onMouseEnter={() => { if (isFinePointer()) onHoverStart(slug); }}
          onClick={() => onHeadClick(slug)}
          className={`relative w-full flex items-center justify-center p-2.5 my-0.5 rounded-md transition-colors ${
            active ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-primary/10 hover:text-foreground'
          } ${flyoutOpen ? 'bg-primary/10 text-foreground' : ''}`}
        >
          <Icon size={18} />
          {badge && (
            <span className="absolute top-0.5 right-1 min-w-[16px] h-4 px-1 rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center">
              {badge}
            </span>
          )}
        </button>
        {flyoutOpen && pos && createPortal(
          <div
            ref={panelRef}
            data-popover={popKey}
            role="menu"
            aria-label={label}
            onMouseEnter={onFlyoutEnter}
            onMouseLeave={() => { if (isFinePointer()) onFlyoutLeave(); }}
            style={{ position: 'fixed', top: pos.top, left: pos.left, minWidth: 224, zIndex: 40 }}
            className={`rounded-lg border border-default bg-card shadow-xl py-1 transition-[opacity,transform] duration-[250ms] ease-out motion-reduce:transition-none ${
              shown ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-1'
            }`}
          >
            <div className="px-3 py-1.5 text-[15px] font-semibold text-foreground flex items-center gap-2">
              <Icon size={16} />
              <span className="flex-1">{label}</span>
              {badge && (
                <span title={badgeTitle} className="min-w-[18px] h-4 px-1 rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center">
                  {badge}
                </span>
              )}
            </div>
            {children}
          </div>,
          getModalPortalTarget(),
        )}
      </li>
    );
  }

  // ----------------------------------------------------------------- breit
  // Kopf = Sektions-Optik (groesser und halbfett als die Eintraege, Trennlinie
  // oben; Joerg 2026-09-17: Koepfe GROESSER als die Eintraege, keine Versalien),
  // Eintraege darunter normale Schrift 14 px, eingerueckt. Body bleibt im DOM und
  // klappt per grid-template-rows weich auf/zu; geschlossen ist er inert.
  return (
    <li data-sidebar-group={slug} data-open={open ? '1' : '0'} data-pinned={pinned ? '1' : '0'} className="mt-1 first:mt-0 border-t border-default/60 first:border-t-0">
      <button
        type="button"
        aria-expanded={open}
        title={pinned ? `${title} (angepinnt — Klick löst)` : `${title} (Klick pinnt die Gruppe offen)`}
        onMouseEnter={() => { if (isFinePointer() && !editMode) onHoverStart(slug); }}
        onClick={() => onHeadClick(slug)}
        className={`relative w-full flex items-center gap-2.5 pl-3 pr-2 pt-2.5 pb-1.5 text-[15px] font-semibold text-left transition-colors ${
          active ? 'text-primary' : open ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        {active && <span aria-hidden className="absolute left-0 top-2 bottom-1 w-0.5 rounded bg-primary" />}
        <Icon size={16} className={`flex-shrink-0 ${active ? 'text-primary' : 'text-muted-foreground'}`} />
        <span className="truncate flex-1">
          {label}
          {countHint !== undefined && countHint > 0 && <span className="ml-1 text-sm font-normal text-muted-foreground">({countHint})</span>}
        </span>
        {!open && badge && (
          <span title={badgeTitle} className="min-w-[18px] h-4 px-1 rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center">
            {badge}
          </span>
        )}
        {pinned && !editMode
          ? <Pin size={11} className="text-primary flex-shrink-0" aria-label="angepinnt" />
          : <ChevronRight size={12} className={`text-muted-foreground flex-shrink-0 transition-transform duration-[350ms] motion-reduce:transition-none ${open ? 'rotate-90' : ''}`} />}
      </button>
      <div
        data-sidebar-group-body={slug}
        aria-hidden={!open}
        {...(open ? {} : { inert: true })}
        className={`grid transition-[grid-template-rows] duration-[350ms] ease-out motion-reduce:transition-none ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
      >
        <div className="overflow-hidden min-h-0 pl-2">{children}</div>
      </div>
    </li>
  );
}
